import { useMemo } from 'react'
import type { TupleToUnion } from 'type-fest'

import { cn } from '@mntn-dev/ui-utilities'

import type { IconSize } from '../icon/icon.tsx'

const buttonSizeNames = ['xs', 'sm', 'md', 'lg', 'xl'] as const
type ButtonSize = TupleToUnion<typeof buttonSizeNames>

const buttonThemeColorNames = [
  'info',
  'notice',
  'positive',
  'negative',
  'caution',
  'link',
] as const
type ButtonThemeColor = TupleToUnion<typeof buttonThemeColorNames>

const buttonTextColorNames = [...buttonThemeColorNames, 'link'] as const
type ButtonTextColor = TupleToUnion<typeof buttonTextColorNames>

const buttonVariantNames = [
  'primary',
  'secondary',
  'tertiary',
  'text',
  'destructive',
  'positive',
  'notice',
] as const
type ButtonVariant = TupleToUnion<typeof buttonVariantNames>

const buttonShapes = ['circle', 'square'] as const
type ButtonShape = TupleToUnion<typeof buttonShapes>

type UseButtonStylesProps = {
  size: ButtonSize
  variant: ButtonVariant
  /** Use when you need the width to be fixed to the height of the button. Make sure your content fits! */
  shape?: ButtonShape
  borderColor?: ButtonThemeColor
  textColor?: ButtonThemeColor
}

const getPrimaryVariantClassName = (borderColor?: ButtonThemeColor) =>
  cn(
    'bgg-highlight-green-10 shadow-black-under-10 text-primary-inverse',
    'hover:bgg-highlight-green-20 hover:shadow-black-under-20',
    'active:bgg-highlight-green-10 active:shadow-none',
    {
      'grad-border-top-highlight-50 hover:grad-border-top-highlight-30 active:grad-border-top-highlight-70':
        !borderColor,
    }
  )

const getSecondaryVariantClassName = (borderColor?: ButtonThemeColor) =>
  cn(
    'bg-button-secondary text-primary shadow-black-under-10',
    'hover:bg-button-secondary-hover hover:shadow-black-under-20',
    'active:bg-button-secondary-active active:shadow-none',
    { 'grad-border-bottom-underlight-subtle': !borderColor }
  )

const getTertiaryClassName = () =>
  cn(
    'bg-button-tertiary text-primary shadow-none',
    'hover:bg-button-tertiary-hover',
    'active:bg-button-tertiary-active'
  )

const getTextClassName = () =>
  cn(
    'bg-button-text text-primary shadow-none',
    'hover:bg-button-text-hover',
    'active:bg-button-text-active'
  )

const getDestructiveClassName = (borderColor?: ButtonThemeColor) =>
  cn(
    'bg-button-destructive text-primary shadow-none',
    'hover:bg-button-destructive-hover',
    'active:bg-button-destructive-active',
    { 'grad-border-bottom-underlight-subtle': !borderColor }
  )

export const getPositiveClassName = (borderColor?: ButtonThemeColor) =>
  cn(
    'bg-button-positive text-primary-inverse shadow-none',
    'hover:bg-button-positive-hover',
    'active:bg-button-positive-active',
    { 'grad-border-bottom-underlight-subtle': !borderColor }
  )

export const getNoticeClassName = (borderColor?: ButtonThemeColor) =>
  cn(
    'bg-button-notice text-primary-inverse shadow-none',
    'hover:bg-button-notice-hover',
    'active:bg-button-notice-active',
    { 'grad-border-bottom-underlight-subtle': !borderColor }
  )

const getBorderColorClassName = (color?: ButtonThemeColor) => {
  if (!color) {
    return undefined
  }

  switch (color) {
    case 'info':
      return 'border border-info hover:border-info-hover active:border-info-press'
    case 'notice':
      return 'border border-notice hover:border-notice-hover active:border-notice-press'
    case 'positive':
      return 'border border-positive hover:border-positive-hover active:border-positive-press'
    case 'negative':
      return 'border border-negative hover:border-negative-hover active:border-negative-press'
    case 'caution':
      return 'border border-caution hover:border-caution-hover active:border-caution-press'
  }
}

const getTextColorClassName = (
  variant: ButtonVariant,
  color?: ButtonTextColor
) => {
  if (!color) {
    return undefined
  }

  switch (color) {
    case 'info':
      return cn('text-info hover:text-info active:text-info', {
        'hover:text-info-hover active:text-info-press': variant === 'text',
      })
    case 'notice':
      return cn('text-notice hover:text-notice active:text-notice', {
        'hover:text-notice-hover active:text-notice-press': variant === 'text',
      })
    case 'positive':
      return cn('text-positive hover:text-positive active:text-positive', {
        'hover:text-positive-hover active:text-positive-press':
          variant === 'text',
      })
    case 'negative':
      return cn('text-negative hover:text-negative active:text-negative', {
        'hover:text-negative-hover active:text-negative-press':
          variant === 'text',
      })
    case 'caution':
      return cn('text-caution hover:text-caution active:text-caution', {
        'hover:text-caution-hover active:text-caution-press':
          variant === 'text',
      })
    case 'link':
      return cn('text-link hover:text-link active:text-link', {
        'hover:text-link-hover active:text-link-pressed': variant === 'text',
      })
  }
}

const getVariantClassName = (
  variant: ButtonVariant,
  borderColor?: ButtonThemeColor
) => {
  switch (variant) {
    case 'primary':
      return getPrimaryVariantClassName(borderColor)
    case 'secondary':
      return getSecondaryVariantClassName(borderColor)
    case 'tertiary':
      return getTertiaryClassName()
    case 'text':
      return getTextClassName()
    case 'destructive':
      return getDestructiveClassName(borderColor)
    case 'positive':
      return getPositiveClassName(borderColor)
    case 'notice':
      return getNoticeClassName(borderColor)
  }
}

const getOverrideClassNames = (
  variant: ButtonVariant,
  borderColor?: ButtonThemeColor,
  textColor?: ButtonTextColor
) => {
  return cn(
    getBorderColorClassName(borderColor),
    getTextColorClassName(variant, textColor)
  )
}

const iconSizeMap: Record<ButtonSize, IconSize> = {
  xs: 'sm',
  sm: 'sm',
  md: 'sm',
  lg: 'md',
  xl: 'md',
}

export const getIconSize = (size: ButtonSize): IconSize => iconSizeMap[size]

const buttonSizeClassMap: Record<ButtonSize, string> = {
  xs: 'button-size-xs',
  sm: 'button-size-sm',
  md: 'button-size-md',
  lg: 'button-size-lg',
  xl: 'button-size-xl',
}

const getButtonSizeClassName = (size: ButtonSize) => buttonSizeClassMap[size]

const buttonShapeClassMap: Record<ButtonShape, string> = {
  circle: 'button-shape-circle',
  square: 'button-shape-square',
}

const getButtonShapeClassName = (shape?: ButtonShape) => {
  if (!shape) {
    return undefined
  }

  return buttonShapeClassMap[shape]
}

const getButtonStyles = (
  size: ButtonSize,
  variant: ButtonVariant,
  shape?: ButtonShape,
  borderColor?: ButtonThemeColor,
  textColor?: ButtonTextColor
) =>
  cn(
    'button-base',
    getButtonSizeClassName(size),
    getVariantClassName(variant, borderColor),
    getOverrideClassNames(variant, borderColor, textColor),
    getButtonShapeClassName(shape)
  )

function useButtonStyles({
  variant,
  size,
  shape,
  borderColor,
  textColor,
}: UseButtonStylesProps) {
  const buttonClassName = useMemo(
    () => getButtonStyles(size, variant, shape, borderColor, textColor),
    [shape, size, variant, borderColor, textColor]
  )

  return { buttonClassName }
}

export {
  type ButtonSize,
  type ButtonThemeColor,
  type ButtonVariant,
  buttonSizeNames,
  buttonThemeColorNames,
  buttonVariantNames,
  getBorderColorClassName,
  getButtonStyles,
  getVariantClassName,
  type UseButtonStylesProps,
  useButtonStyles,
}
